import Alert      from '../components/alert';
import classnames from 'classnames';
import React      from 'react';

// Maps customer record field name to the label that will be shown in the
// dropbox.
const fieldDisplayNames = {
  id:              'Customer ID',
  full:            'Full name',
  given:           'First (given) name',
  surname:         'Last name',
  preferred:       'Preferred name/nickname',
  email:           'Email address',
  phone:           'Phone number',
  provider:        'Team member',
  customerSince:   'Customer since',
  lastTransaction: 'Date of last transaction'
};

const sections = [
  {
    label:       'CUSTOMER NAME',
    fields:      [ 'given', 'surname', 'full' ],
    conditional: 'full'
  },
  {
    label:  'CONTACT INFO',
    fields: [ 'email', 'phone' ]
  },
  {
    label:  'OPTIONAL',
    fields: [ 'provider', 'id', 'customerSince', 'lastTransaction' ]
  }
];

const MapContext = React.createContext();

export default function MapFields({ onMappingChange, fileName, mapping = {}, headers, summary, errors, records }) {
  function onChange({ target }) {
    const { name, value } = target;
    const newMapping      = { ...mapping, [name]: value  };

    onMappingChange({ mapping: newMapping });
  }

  return (
    <div className="map-fields">
      <MapContext.Provider value={{ headers, records, mapping: mapping || {}, onChange }}>
        <Alert>
          <div>
            Match your columns with the correct Broadly fields. {fileName && (<span>Your file: <b>{fileName}</b></span>)}
          </div>
        </Alert>
        {sections.map(section => <Section key={section.label} errors={errors} section={section}/>)}
      </MapContext.Provider>
      <Summary summary={summary}/>
    </div>
  );
}

function Section({ section, errors }) {
  const { label }       = section;
  const { fields }      = section;
  const { conditional } = section;

  const errorsInSection = errors && Object.entries(errors)
    .filter(([ key ]) => fields.includes(key))
    .map(([ , value ]) => value);
  const hasErrors       = errors && errorsInSection.length > 0;

  const className = classnames('map-fields__section', { 'map-fields__section-with-error': hasErrors });
  return (
    <div className={className}>
      {hasErrors && <Errors errors={errorsInSection}/>}
      <table className="map-fields__table">
        <thead>
          <tr>
            <th className="map-fields__table__left__header map-fields__table__left">{label}</th>
            <th className="map-fields__table__right__header map-fields__table__right">COLUMN FROM YOUR FILE</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(field => <Field key={field} field={field} conditional={field === conditional}/>)}
        </tbody>
      </table>
    </div>
  );
}


function Errors({ errors }) {
  return (
    <div className="map-fields__error">
      <ul>
        {errors.map((error, i) => (<li key={i}>{error}</li>))}
      </ul>
    </div>
  );
}

function Field({ field, conditional }) {
  const className = classnames({ 'map-fields__table__conditional': conditional });

  return (
    <tr className={className}>
      <td className="map-fields__table__left">
        {fieldDisplayNames[field]}
      </td>
      <td className="map-fields__table__right">
        <ChooseField field={field}/>
      </td>
    </tr>
  );
}


function ChooseField({ field }) {
  return (
    <MapContext.Consumer>{
      ({ mapping, ...props }) => {
        const selected = mapping[field] || '';

        return (
          <React.Fragment>
            <SelectField field={field} mapping={mapping} selected={selected} {...props}/>
            {selected && <ExampleRecord selected={selected} {...props}/>}
          </React.Fragment>
        );
      }
    }
    </MapContext.Consumer>
  );
}


function SelectField({ field, mapping, selected, headers, onChange }) {
  const selectedHeader = selected && headers.find(({ value }) => value === selected);

  return (
    <div className="map_field__select">
      <select name={field} value={selected} onChange={onChange}>
        <option key="" value="">No matching column (Skip)</option>
        {selectedHeader && (<option key={selected} value={selected}>{selectedHeader.label}</option>)}
        <AvailableFields mapping={mapping} headers={headers}/>
      </select>
    </div>
  );
}


function AvailableFields({ mapping, headers }) {
  const headersSelected = Object.values(mapping);

  return headers.reduce((acc, header) => {
    if (!headersSelected.includes(header.value))
      acc.push(<option key={header.value} value={header.value}>{header.label}</option>);

    return acc;
  }, []);
}


function ExampleRecord({ headers, records, selected }) {
  if (records.length) {
    const index         = headers.findIndex(({ value }) => value === selected);
    const exampleRecord = records.find(record => !!record[index]);
    const example       = exampleRecord && exampleRecord[index];

    return (
      <div className="map-fields__example_record">
        {example && `ex: ${example}`}
        {!example && 'No data'}
      </div>
    );
  } else
    return null;
}

function Summary({ summary }) {
  if (!summary)
    return null;


  const { ready }       = summary;
  const { missingInfo } = summary;

  return (
    <div className="map-fields__summary">
      <ul>
        {ready > 0 && (
          <li>
            <i className="icons8 icons8-check-mark-symbol map-fields__summary_icon_ready"></i>
            {ready} {ready === 1 ? 'customer is' : 'customers are'} ready to be imported
          </li>
        )}
        {missingInfo > 0 && (
          <li>
            <i className="icons8 icons8-minus map-fields__summary_icon_missing_info"></i>
            {missingInfo} {missingInfo === 1 ? 'customer is' : 'customers are'} missing name or contact info and will not be imported
          </li>
        )}
        {missingInfo > 0 && (
          <li>
            <i className="icons8 icons8-info map-fields__summary_icon_info"></i>
            If you would like to fix {missingInfo === 1 ? 'this record' : 'these records'}: cancel, edit and reupload your file
          </li>
        )}
      </ul>
    </div>
  );
}
